import { supabase } from '@/integrations/supabase/client';

export interface Debate {
  id: string;
  interview_id: string;
  creator_user_id: string;
  invited_user_id?: string;
  invited_username?: string;
  assertion: string;
  status: 'pending' | 'active' | 'completed' | 'cancelled';
  created_at: string;
  updated_at: string;
  creator_profile?: {
    display_name: string;
    avatar_url?: string;
  };
  invited_profile?: {
    display_name: string;
    avatar_url?: string;
  };
  arguments?: DebateArgument[];
  votes?: DebateVote[];
}

export interface DebateArgument {
  id: string;
  debate_id: string;
  user_id: string | null;
  argument_number: number;
  argument_text: string;
  created_at: string;
  anonymous_name?: string;
  profiles?: {
    display_name: string;
    avatar_url?: string;
  };
}

export interface DebateVote {
  id: string;
  debate_id: string;
  voter_user_id: string;
  voted_for_user_id: string;
  created_at: string;
}

export interface CreateDebateData {
  interview_id: string;
  assertion: string;
  invited_username?: string;
}

export class DebatesService {
  static async getDebatesForInterview(interviewId: string): Promise<Debate[]> {
    const { data, error } = await supabase
      .from('debates')
      .select(`
        *,
        creator_profile:profiles!debates_creator_user_id_fkey (
          display_name,
          avatar_url
        ),
        invited_profile:profiles!debates_invited_user_id_fkey (
          display_name,
          avatar_url
        )
      `)
      .eq('interview_id', interviewId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching debates:', error);
      throw error;
    }

    // Fetch arguments and votes for each debate
    const debatesWithDetails = await Promise.all(
      (data || []).map(async (debate) => {
        const [debateArguments, votes] = await Promise.all([
          this.getDebateArguments(debate.id),
          this.getDebateVotes(debate.id),
        ]);
        return { 
          ...debate, 
          arguments: debateArguments, 
          votes,
          creator_profile: debate.creator_profile || { display_name: 'Unknown', avatar_url: undefined },
          invited_profile: debate.invited_profile || { display_name: 'Unknown', avatar_url: undefined }
        } as Debate;
      })
    );

    return debatesWithDetails;
  }

  static async getDebateArguments(debateId: string): Promise<DebateArgument[]> {
    // Fetch arguments without nested profile select to avoid FK issues
    const { data, error } = await supabase
      .from('debate_arguments')
      .select('*')
      .eq('debate_id', debateId)
      .order('argument_number', { ascending: true });

    if (error) {
      console.error('Error fetching debate arguments:', error);
      return [];
    }

    // Fetch profiles separately for arguments with user_id
    const argsWithProfiles = await Promise.all(
      (data || []).map(async (arg) => {
        let profiles = { display_name: arg.anonymous_name || 'Anonym', avatar_url: undefined as string | undefined };
        
        if (arg.user_id) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('display_name, avatar_url')
            .eq('user_id', arg.user_id)
            .single();
          
          if (profile) {
            profiles = profile;
          }
        }
        
        return { ...arg, profiles };
      })
    );

    return argsWithProfiles as DebateArgument[];
  }

  static async getDebateVotes(debateId: string): Promise<DebateVote[]> {
    const { data, error } = await supabase
      .from('debate_votes')
      .select('*')
      .eq('debate_id', debateId);

    if (error) {
      console.error('Error fetching debate votes:', error);
      return [];
    }

    return data || [];
  }

  static async createDebate(debateData: CreateDebateData, anonymousCreatorName?: string): Promise<Debate | null> {
    const { data: { user } } = await supabase.auth.getUser();

    // Find invited user if username provided
    let invitedUserId = null;
    if (debateData.invited_username) {
      const { data: invitedUser } = await supabase
        .from('profiles')
        .select('user_id')
        .eq('username', debateData.invited_username)
        .single();
      
      invitedUserId = invitedUser?.user_id;
    }

    const insertData = {
      interview_id: debateData.interview_id,
      creator_user_id: user?.id || null,
      invited_user_id: invitedUserId,
      invited_username: debateData.invited_username,
      assertion: debateData.assertion,
      status: invitedUserId ? 'pending' as const : 'active' as const,
      creator_anonymous_name: !user && anonymousCreatorName ? anonymousCreatorName : undefined,
    };

    const { data, error } = await supabase
      .from('debates')
      .insert(insertData)
      .select('*')
      .single();

    if (error) {
      console.error('Error creating debate:', error);
      throw error;
    }

    // Fetch profiles separately
    let creator_profile = { display_name: anonymousCreatorName || 'Anonym', avatar_url: undefined as string | undefined };
    let invited_profile = { display_name: 'Unknown', avatar_url: undefined as string | undefined };

    if (data.creator_user_id) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('display_name, avatar_url')
        .eq('user_id', data.creator_user_id)
        .single();
      if (profile) creator_profile = profile;
    }

    if (data.invited_user_id) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('display_name, avatar_url')
        .eq('user_id', data.invited_user_id)
        .single();
      if (profile) invited_profile = profile;
    }

    return {
      ...data,
      creator_profile,
      invited_profile
    } as Debate;
  }

  static async getAllActiveDebates(limit = 20): Promise<(Debate & { interview?: { id: string; title: string; interviewee_name: string }; argument_count: number })[]> {
    const { data, error } = await supabase
      .from('debates')
      .select('*')
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching all active debates:', error);
      return [];
    }

    // Collect unique interview IDs
    const interviewIds = [...new Set((data || []).map(d => d.interview_id))];
    
    // Batch fetch interviews (RLS may block unpublished, so we handle that)
    const { data: interviews } = await supabase
      .from('interviews')
      .select('id, title, interviewee_name')
      .in('id', interviewIds);
    
    const interviewMap = new Map((interviews || []).map(i => [i.id, i]));

    // Enrich with profiles and argument count
    const enrichedDebates = await Promise.all(
      (data || []).map(async (debate) => {
        // Fetch creator profile
        let creator_profile = { display_name: (debate as any).creator_anonymous_name || 'Anonym', avatar_url: undefined as string | undefined };
        if (debate.creator_user_id) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('display_name, avatar_url')
            .eq('user_id', debate.creator_user_id)
            .single();
          if (profile) creator_profile = profile;
        }

        // Get interview from map, or create minimal fallback using interview_id
        const interview = interviewMap.get(debate.interview_id) || { 
          id: debate.interview_id, 
          title: 'Artikkel', 
          interviewee_name: '' 
        };

        // Fetch argument count
        const { count } = await supabase
          .from('debate_arguments')
          .select('id', { count: 'exact', head: true })
          .eq('debate_id', debate.id);

        return {
          ...debate,
          creator_profile,
          invited_profile: { display_name: 'Unknown', avatar_url: undefined },
          interview,
          argument_count: count || 0,
        };
      })
    );

    return enrichedDebates as (Debate & { interview?: { id: string; title: string; interviewee_name: string }; argument_count: number })[];
  }

  static async addArgument(debateId: string, argumentText: string, anonymousName?: string): Promise<DebateArgument | null> {
    const { data: { user } } = await supabase.auth.getUser();

    // Get total argument count for numbering (across all users)
    const { data: existingArgs } = await supabase
      .from('debate_arguments')
      .select('argument_number')
      .eq('debate_id', debateId)
      .order('argument_number', { ascending: false })
      .limit(1);

    const nextArgumentNumber = existingArgs && existingArgs.length > 0 
      ? existingArgs[0].argument_number + 1 
      : 1;

    const insertData: {
      debate_id: string;
      user_id: string | null;
      argument_number: number;
      argument_text: string;
      anonymous_name?: string;
    } = {
      debate_id: debateId,
      user_id: user?.id || null,
      argument_number: nextArgumentNumber,
      argument_text: argumentText,
    };

    // Add anonymous name if user is not authenticated
    if (!user && anonymousName) {
      insertData.anonymous_name = anonymousName;
    }

    // IMPORTANT:
    // For anonymous users we must avoid selecting nested `profiles`, since it can fail due to RLS
    // even when the insert into `debate_arguments` is allowed.
    const baseQuery = supabase
      .from('debate_arguments')
      .insert(insertData);

    const { data, error } = user
      ? await baseQuery
          .select(
            `
            *,
            profiles (
              display_name,
              avatar_url
            )
          `
          )
          .single()
      : await baseQuery
          .select('*')
          .single();

    if (error) {
      console.error('Error adding argument:', error);
      throw error;
    }

    return {
      ...data,
      profiles: (data as any)?.profiles || { display_name: anonymousName || 'Anonym', avatar_url: undefined }
    } as DebateArgument;
  }

  static async voteForDebate(debateId: string, votedForUserId: string): Promise<boolean> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User must be authenticated to vote');
    }

    // Remove existing vote if any
    await supabase
      .from('debate_votes')
      .delete()
      .eq('debate_id', debateId)
      .eq('voter_user_id', user.id);

    // Add new vote
    const { error } = await supabase
      .from('debate_votes')
      .insert({
        debate_id: debateId,
        voter_user_id: user.id,
        voted_for_user_id: votedForUserId,
      });

    if (error) {
      console.error('Error voting:', error);
      throw error;
    }

    return true;
  }

  static async updateDebateStatus(debateId: string, status: Debate['status']): Promise<boolean> {
    const { error } = await supabase
      .from('debates')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', debateId);

    if (error) {
      console.error('Error updating debate status:', error);
      throw error;
    }

    return true;
  }
}